<template>
  <div class="kpi-filter">
    <div class="kpi-filter__field">
      <FeldLabel :icon="labelIcons.Globe" text="Region" />
      <RestriktionenSegmentedControl :options="regionOptions" :value="draft.region" @input="onRegionChange" />
    </div>
    <div v-if="!isHistoryPage" class="kpi-filter__field">
      <FeldLabel :icon="labelIcons.MapPinPlus" text="Reisekürzel" />
      <AppSelect
        label="Reisekürzel"
        placeholder="Reisekürzel"
        multiple
        inline
        sort="selected-first"
        list-max-height="180px"
        :disabled="isBusy"
        :options="kuerzelFilterOptions"
        :value="kuerzelDraftValues"
        @input="onKuerzelInput"
      />
    </div>
    <div class="kpi-filter__field">
      <FeldLabel :icon="labelIcons.PlaneTakeoff" text="Abflughafen" />
      <div class="kpi-filter__chips">
        <button
          v-for="deutscherAbflughafen in deutscheAbflughaefen"
          :key="deutscherAbflughafen"
          :id="deutscherAbflughafen"
          type="button"
          class="kpi-filter__chip"
          :class="{
            'kpi-filter__chip--active': isAbflughafenAktiv(deutscherAbflughafen),
          }"
          :disabled="isBusy"
          @click="onDeutscherAbflughafenFilterClick"
        >
          {{ deutscherAbflughafen }}
        </button>
      </div>
    </div>
    <div class="kpi-filter__field">
      <FeldLabel :icon="labelIcons.Flag" text="Priorität" />
      <div class="kpi-filter__chips kpi-filter__chips--equal">
        <button
          v-for="prioritaet in prioritaeten"
          :key="prioritaet.value"
          :id="prioritaet.name"
          type="button"
          class="kpi-filter__chip"
          :class="{ 'kpi-filter__chip--active': isPrioritaetAktiv(prioritaet) }"
          :disabled="isBusy"
          @click="onPrioritaetFilterClick"
        >
          {{ prioritaet.name }}
        </button>
      </div>
    </div>
    <div class="kpi-filter__field">
      <FeldLabel :icon="labelIcons.UserRound" text="Regionsmanagerin" />
      <div class="kpi-filter__chips kpi-filter__chips--equal">
        <button
          v-for="regionsmanager in regionsmanagers"
          :key="regionsmanager"
          :id="regionsmanager"
          type="button"
          class="kpi-filter__chip"
          :class="{ 'kpi-filter__chip--active': isRegionsmanagerAktiv(regionsmanager) }"
          :disabled="isBusy"
          @click="onRegionsmanagerFilterClick"
        >
          {{ regionsmanager }}
        </button>
      </div>
    </div>
    <div class="kpi-filter__switch-row">
      <FeldLabel :icon="labelIcons.CircleCheck" text="Nur akzeptiert" />
      <b-form-checkbox v-model="draft.nurAkzeptiert" :disabled="isBusy" class="mr-n2" switch />
    </div>
    <div class="d-flex" style="gap: 8px">
      <b-button class="dialog-btn-secondary flex-grow-1" :disabled="isBusy" @click="$emit('cancel')">
        Abbrechen
      </b-button>
      <b-button
        :variant="!hasChanges ? 'secondary' : 'primary'"
        class="dialog-btn flex-grow-1"
        :disabled="!hasChanges"
        @click="onApply"
      >
        Anwenden
      </b-button>
    </div>
  </div>
</template>
<script>
import {
  SET_REISEN_FILTER,
  SET_DEUTSCHER_ABFLUGHAFEN_FILTER,
  SET_PRIORITAET_FILTER,
  SET_REGION_FILTER,
  RESET_REGION_FILTER,
  SET_REGIONSMANAGER_FILTER,
  SET_NUR_AKZEPTIERT_FILTER,
} from '@/core/flugverfuegbarkeit/stores/kpi-fvc.module.js';
import AppSelect from '@/components/common/app-select/app-select.vue';
import RestriktionenSegmentedControl from '@/components/restriktionen/restriktionen-segmented-control.vue';
import FeldLabel from '@/components/common/feld-label.vue';
import { Globe, MapPinPlus, PlaneTakeoff, Flag, UserRound, CircleCheck } from 'lucide-vue';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'KPIFilter',
  components: { AppSelect, RestriktionenSegmentedControl, FeldLabel },
  props: { isHistoryPage: { type: Boolean } },
  data() {
    return {
      labelIcons: Object.freeze({ Globe, MapPinPlus, PlaneTakeoff, Flag, UserRound, CircleCheck }),
      regions: ['Gesamt', 'Fern', 'EU'],
      deutscheAbflughaefen: ['Alle', 'HAM', 'FRA', 'MUC', 'DUS', 'BER', 'VIE', 'ZRH'],
      prioritaeten: [
        { name: 'Alle', value: 4 },
        { name: 'A', value: 0 },
        { name: 'B', value: 1 },
        { name: 'C', value: 2 },
        { name: 'D', value: 3 },
      ],
      regionsmanagers: ['Alle', 'STGE', 'SYKE'],
      fernRegions: ['Asien', 'Amerika', 'Ozeanien', 'Afrika', 'Orient'],
      euRegions: ['Südeuropa', 'Nordeuropa', 'Dach'],
      // Auswahl wird lokal gehalten und erst mit Anwenden in den Store übernommen
      draft: {
        region: 'Gesamt',
        reisen: [],
        deutscherAbflughafen: [],
        prioritaet: [],
        regionsmanager: [],
        nurAkzeptiert: false,
      },
    };
  },
  computed: {
    ...mapGetters(['getAllGroupedData']),
    ...mapState({
      isBusy: state => state.kpiFvc.isBusy,
      filters: state => state.kpiFvc.filters,
    }),
    regionOptions() {
      return this.regions.map(region => ({ value: region, text: region }));
    },
    hasChanges() {
      return (
        this.draft.region !== this.getRegionFromStore() ||
        this.draft.nurAkzeptiert !== this.filters.nurAkzeptiert ||
        !this.isSameSelection(
          this.draft.reisen.map(reise => reise.kuerzelLabel),
          this.filters.reisen.map(reise => reise.kuerzelLabel)
        ) ||
        !this.isSameSelection(this.draft.deutscherAbflughafen, this.filters.deutscherAbflughafen) ||
        !this.isSameSelection(this.draft.prioritaet, this.filters.prioritaet) ||
        !this.isSameSelection(this.draft.regionsmanager, this.filters.regionsmanager)
      );
    },
    kuerzelDraftValues() {
      return this.draft.reisen.map(reise => reise.kuerzelLabel);
    },
    // Optionen richten sich nach der gewählten Region
    kuerzelFilterOptions() {
      if (this.draft.region === 'Fern') {
        return this.mapKuerzelOptions(this.fernRegions);
      }
      if (this.draft.region === 'EU') {
        return this.mapKuerzelOptions(this.euRegions);
      }
      return (this.$store.state.kpiFvc.reiseFilterOptions ?? []).map(filter => ({
        value: filter.reisekuerzel,
        text: filter.reisekuerzel,
      }));
    },
  },
  created() {
    this.syncDraftFromStore();
  },
  methods: {
    // Übernimmt die aktuell angewendeten Filter aus dem Store in den Draft
    syncDraftFromStore() {
      this.draft = {
        region: this.getRegionFromStore(),
        reisen: [...this.filters.reisen],
        deutscherAbflughafen: [...this.filters.deutscherAbflughafen],
        prioritaet: [...this.filters.prioritaet],
        regionsmanager: [...this.filters.regionsmanager],
        nurAkzeptiert: this.filters.nurAkzeptiert,
      };
    },
    isSameSelection(draftValues, appliedValues) {
      return (
        draftValues.length === appliedValues.length &&
        JSON.stringify([...draftValues].sort()) === JSON.stringify([...appliedValues].sort())
      );
    },
    getRegionFromStore() {
      if (this.filters.region.length === 0) return 'Gesamt';
      return this.filters.region.includes(this.euRegions[0]) ? 'EU' : 'Fern';
    },
    mapKuerzelOptions(regions) {
      const regionSet = new Set(regions);
      const kuerzel = new Set();
      this.getAllGroupedData.forEach(item => {
        if (regionSet.has(item.region)) kuerzel.add(item.reisekuerzel);
      });
      return [...kuerzel]
        .sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))
        .map(reisekuerzel => ({ value: reisekuerzel, text: reisekuerzel }));
    },
    isAbflughafenAktiv(abflughafen) {
      return (
        this.draft.deutscherAbflughafen.includes(abflughafen) ||
        (this.draft.deutscherAbflughafen.length === 0 && abflughafen === 'Alle')
      );
    },
    isPrioritaetAktiv(prioritaet) {
      return (
        this.draft.prioritaet.includes(prioritaet.value) ||
        (this.draft.prioritaet.length === 0 && prioritaet.name === 'Alle')
      );
    },
    isRegionsmanagerAktiv(regionsmanager) {
      return (
        this.draft.regionsmanager.includes(regionsmanager) ||
        (this.draft.regionsmanager.length === 0 && regionsmanager === 'Alle')
      );
    },
    onRegionChange(region) {
      if (this.draft.region === region) return;
      this.draft.region = region;
      // Kürzel entfernen, die in der neuen Region nicht mehr auswählbar sind
      const verfuegbareKuerzel = this.kuerzelFilterOptions.map(option => option.value);
      this.draft.reisen = this.draft.reisen.filter(reise => verfuegbareKuerzel.includes(reise.kuerzelLabel));
    },
    // Das Select liefert die Reisekürzel als Werte, der Store erwartet { kuerzelId, kuerzelLabel }
    onKuerzelInput(reisekuerzel) {
      this.draft.reisen = reisekuerzel.map(kuerzel => ({
        kuerzelId: kuerzel,
        kuerzelLabel: kuerzel,
      }));
    },
    onDeutscherAbflughafenFilterClick($event) {
      const { id: abflughafen } = $event.target;
      if (abflughafen === 'Alle') {
        this.draft.deutscherAbflughafen = [];
        return;
      }
      this.draft.deutscherAbflughafen = this.toggleValue(this.draft.deutscherAbflughafen, abflughafen);
      // Wenn alle angeklickt sind, resette
      if (this.draft.deutscherAbflughafen.length === this.deutscheAbflughaefen.length - 1) {
        this.draft.deutscherAbflughafen = [];
      }
    },
    onPrioritaetFilterClick($event) {
      const { id } = $event.target;
      if (id === 'Alle') {
        this.draft.prioritaet = [];
        return;
      }
      const prioritaet = this.prioritaeten.find(item => item.name === id).value;
      this.draft.prioritaet = this.toggleValue(this.draft.prioritaet, prioritaet);
      // Wenn alle angeklickt sind, resette
      if (this.draft.prioritaet.length === this.prioritaeten.length - 1) {
        this.draft.prioritaet = [];
      }
    },
    onRegionsmanagerFilterClick($event) {
      const { id: regionsmanager } = $event.target;
      if (regionsmanager === 'Alle') {
        this.draft.regionsmanager = [];
        return;
      }
      this.draft.regionsmanager = this.toggleValue(this.draft.regionsmanager, regionsmanager);
      // Wenn alle angeklickt sind, resette
      if (this.draft.regionsmanager.length === this.regionsmanagers.length - 1) {
        this.draft.regionsmanager = [];
      }
    },
    toggleValue(values, value) {
      return values.includes(value) ? values.filter(item => item !== value) : [...values, value];
    },
    // Schreibt den Entwurf in den Store, die Charts filtern sich daraufhin neu
    onApply() {
      if (this.draft.region === 'Fern') {
        this.$store.commit(SET_REGION_FILTER, this.fernRegions);
      } else if (this.draft.region === 'EU') {
        this.$store.commit(SET_REGION_FILTER, this.euRegions);
      } else {
        this.$store.commit(RESET_REGION_FILTER);
      }
      this.$store.commit(SET_REISEN_FILTER, this.draft.reisen);
      this.$store.commit(SET_DEUTSCHER_ABFLUGHAFEN_FILTER, this.draft.deutscherAbflughafen);
      this.$store.commit(SET_PRIORITAET_FILTER, this.draft.prioritaet);
      this.$store.commit(SET_REGIONSMANAGER_FILTER, this.draft.regionsmanager);
      this.$store.commit(SET_NUR_AKZEPTIERT_FILTER, this.draft.nurAkzeptiert);
      this.$emit('apply');
    },
  },
};
</script>

<style scoped>
.kpi-filter {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 16px;
}

.kpi-filter__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.kpi-filter__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kpi-filter__chips--equal .kpi-filter__chip {
  flex: 1;
  min-width: 0;
  padding: 0 8px;
}

.kpi-filter__chip {
  height: 32px;
  padding: 0 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-body);
  cursor: pointer;
}

.kpi-filter__chip:hover:not(:disabled) {
  background: var(--surface-hover);
}

.kpi-filter__chip--active {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.kpi-filter__chip:disabled {
  opacity: 0.6;
  cursor: default;
}

.kpi-filter__switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--border);
  padding-top: 16px;
}
</style>
